import apiClient from '../../services/apiClient';

// BP 22 — class sessions + class activities. Student surface only (the /class
// page and the lecture-player panel); the instructor run-sheet
// (pages/teach/TeachSessions, TeachSessionRun) calls the same backend but
// through its own thin wrappers below, kept in this one file since both sides
// share the same shapes.

export type SessionType = 'intro' | 'lecture' | 'review' | 'test' | 'reading';
export type AttendancePolicy = 'required' | 'one_of_group' | 'none';
export type SessionStatus = 'planned' | 'live' | 'closed';

export interface ClassSession {
  id: string;
  seq: number;
  label: string;
  session_type: SessionType;
  scheduled_date: string | null;
  status: SessionStatus;
  lesson_id: string | null;
  activity_expected: boolean;
  attendance_policy: AttendancePolicy;
}

export interface ClassSessionFull extends ClassSession {
  course_id: string;
  lesson_number: number | null;
  lesson_part: string | null;
  starts_at: string | null;
  ends_at: string | null;
  attendance_group: string | null;
  due_items: string[];
  opens_items: string[];
  date_edited_by: string | null;
  notes: string | null;
  created_at?: string;
  updated_at?: string;
}

export type StepMode = 'instruct' | 'poll' | 'response' | 'group';

export interface ActivityStep {
  key: string;
  label: string;
  minutes: number;
  mode: StepMode;
  prompt: string;
  options?: string[];
  multi?: boolean;
  numeric?: boolean;
  correct?: number | number[];
  // Live-activity set (design §11.2) — an ALLOWLIST governs which of these
  // reach a student (backend studentStep()); this type describes the union
  // an instructor payload may carry. Student-visible: revote_of, confidence,
  // answer. Instructor-only (never sent to a student): misconception_choices,
  // misconception_range.
  /** Points at an earlier step's key this one re-votes (WIP-010). */
  revote_of?: string;
  /** Ask for a confidence tag alongside the answer (WIP-012). */
  confidence?: boolean;
  /** The response control is a lab widget, not the built-in controls (WIP-016). */
  answer?: 'widget';
  /**
   * This step's OWN widget, overriding the activity's.
   *
   * A sixteen-card sort has one prompt per card and needs one model per card;
   * before this existed, `activity.widget_refs` was rendered for every step and
   * card 16 showed card 1's model. Optional — a step without it falls back to
   * the activity's, which is right when the widget really is shared scenery.
   * See `widgetForStep.ts`.
   */
  widget_refs?: WidgetRefConfig[];
  /**
   * Instructor-only ANSWER KEY: what a right answer to this step SAYS, in
   * the instructor's own words. Revealed on the run sheet by a deliberate
   * click (StepAnswerPanel), never auto-shown and never sent to a student.
   *
   * Two fields rather than one, because a poll answer and a written answer
   * are different things: `correct` above points at an option INDEX, which
   * only exists for a step with options and is what the summarisers score
   * against; this carries the model solution for a free-text or numeric
   * `response` step, and the worked reasoning for a poll.
   *
   * Not to be confused with `answer` above — that says which CONTROL to
   * render for the student and IS student-visible.
   */
  expected_answer?: string;
  /** Instructor-only: which option indices indicate the named misconception (WIP-014). */
  misconception_choices?: number[];
  /** Instructor-only: a numeric-answer range indicating the misconception (WIP-014). */
  misconception_range?: [number, number];
}

export interface WidgetRefConfig {
  type: string;
  config?: Record<string, unknown>;
}

/** `payload.widget` shape for a step whose answer is a lab widget's own
 *  state (WIP-016, design §8.3/§11.3). `state` is opaque and untrusted —
 *  validated server-side (`validateWidgetPayload.js`), never interpreted by
 *  this file. `display` is capped at 200 characters. */
export interface WidgetAnswerPayload {
  type: string;
  state: unknown;
  display: string;
}

export type ActivityFormat =
  | 'warmup_retrieval' | 'think_pair_share' | 'peer_instruction' | 'jigsaw' | 'pogil'
  | 'error_hunt' | 'whiteboard_rotation' | 'poe' | 'gallery_walk' | 'problem_set'
  | 'relay' | 'card_sort' | 'case_study' | 'studio' | 'quiz_bowl' | 'exit_ticket'
  | 'onboarding';

export type Grouping = 'solo' | 'pair' | 'small_group' | 'whole_class';
export type Capture = 'none' | 'response' | 'poll' | 'upload';

export interface ClassActivity {
  id: string;
  class_session_id: string;
  index: number;
  title: string;
  format: ActivityFormat;
  grouping: Grouping;
  duration_min: number;
  steps: ActivityStep[];
  widget_refs: WidgetRefConfig[];
  materials: string | null;
  instructor_role: string | null;
  watch_for: string | null;
  misconception: string | null;
  capture: Capture;
  is_live: boolean;
  live_step: number | null;
  /** design §1/§11.1 (migration 018) — the live step's vote is closed and the
   * distribution is on the wall. Reset to false on every go-live/stop. */
  results_shown: boolean;
  /** migration 019 — the instructor's own "done for this meeting" tick
   * (run-sheet tabs). Server-backed, not localStorage: survives a device
   * switch mid-class. Independent of is_live/results_shown — never reset by
   * go-live/stop. */
  completed: boolean;
  /** migration 021 — the step KEYS the instructor has marked done, for the
   * run-sheet's step stack. Keys rather than indices so a later edit that
   * inserts or reorders a step never re-points an existing tick. `goLive`
   * adds the step being LEFT when advancing within an activity; everything
   * else is the instructor's own tick. */
  steps_done: string[];
  source_anchor?: Record<string, unknown> | null;
  assessment_item_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ActivityResponseRecord {
  step_key: string;
  group_label?: string | null;
  payload: Record<string, unknown>;
  submitted_at: string;
}

/** design §6.6 — meeting-level, not activity-level: present even when
 * nothing is currently live, as long as the student has a published group. */
export interface MyGroup {
  label: string;
}

export interface ClassLiveResponse {
  session: ClassSession | null;
  activity: ClassActivity | null;
  my_responses: ActivityResponseRecord[];
  my_group: MyGroup | null;
}

// ── student ──────────────────────────────────────────────────────────────

export async function getSessions(): Promise<ClassSession[]> {
  const res = await apiClient.get('/api/v1/class-sessions');
  return res.data.sessions;
}

export async function getToday(): Promise<ClassSession | null> {
  const res = await apiClient.get('/api/v1/class-sessions/today');
  return res.data.session;
}

export async function getSessionActivities(sessionId: string): Promise<ClassActivity[]> {
  const res = await apiClient.get(`/api/v1/class-sessions/${sessionId}/activities`);
  return res.data.activities;
}

export async function getLive(): Promise<ClassLiveResponse> {
  const res = await apiClient.get('/api/v1/class/live');
  return res.data;
}

export interface SubmitResponseBody {
  step_key: string;
  payload: Record<string, unknown>;
  group_label?: string;
}

export async function submitResponse(activityId: string, body: SubmitResponseBody): Promise<ActivityResponseRecord> {
  const res = await apiClient.post(`/api/v1/activities/${activityId}/responses`, body);
  return res.data.response;
}

// ── instructor (Teach: TeachSessions / TeachSessionRun) ─────────────────────

export interface GenerateResult {
  sessions: { created: number; updated: number; skipped: number };
  /** `answers_filled` counts answer-key fields written onto activities the
   *  instructor has already touched — which includes every activity that has
   *  ever been run in class, since going live writes to the row. Those rows
   *  are otherwise skipped wholesale, so this number is how the instructor
   *  sees that the keys reached the meetings already taught. */
  activities: { created: number; updated: number; skipped: number; unresolved: number; answers_filled: number };
  /** What the generate actually did to the calendar (BP 22 / WIP-001).
   *  `closures_ignored` are dates that changed nothing — not a Tue/Thu, or
   *  outside the term — and the instructor needs to be told, not reassured. */
  schedule?: {
    first_date: string | null;
    last_date: string | null;
    closures_applied: string[];
    closures_ignored: string[];
  };
}

/** The persisted term closure list. Instructor-gated (see the route comment):
 *  Setup is instructor-accessible, platform-admin settings is not. */
export async function getClosures(): Promise<string[]> {
  const res = await apiClient.get('/api/v1/class-sessions/closures');
  return res.data.closures;
}

export async function saveClosures(closures: string[]): Promise<string[]> {
  const res = await apiClient.put('/api/v1/class-sessions/closures', { closures });
  return res.data.closures;
}

export async function generateSessions(body: { source?: 'meetings' | 'weeks'; start_date?: string; closures?: string[] } = {}): Promise<GenerateResult> {
  const res = await apiClient.post('/api/v1/class-sessions/generate', body);
  return res.data;
}

export async function shiftSessions(body: { from_seq: number; by_meetings: number }): Promise<{ updated: number }> {
  const res = await apiClient.post('/api/v1/class-sessions/shift', body);
  return res.data;
}

export async function updateSession(id: string, patch: Partial<ClassSessionFull>): Promise<ClassSessionFull> {
  const res = await apiClient.put(`/api/v1/class-sessions/${id}`, patch);
  return res.data.session;
}

export async function startSession(id: string): Promise<ClassSessionFull> {
  const res = await apiClient.post(`/api/v1/class-sessions/${id}/start`);
  return res.data.session;
}

export async function endSession(id: string): Promise<ClassSessionFull> {
  const res = await apiClient.post(`/api/v1/class-sessions/${id}/end`);
  return res.data.session;
}

export async function createActivity(sessionId: string, body: Partial<ClassActivity>): Promise<ClassActivity> {
  const res = await apiClient.post(`/api/v1/class-sessions/${sessionId}/activities`, body);
  return res.data.activity;
}

export async function updateActivity(id: string, patch: Partial<ClassActivity>): Promise<ClassActivity> {
  const res = await apiClient.put(`/api/v1/activities/${id}`, patch);
  return res.data.activity;
}

/**
 * Move an activity one place within its meeting. Server-side this is a swap of
 * two rows in one transaction — never a delete-and-recreate, which could lose an
 * instructor's activities if a recreate failed after the deletes committed.
 */
export async function moveActivity(id: string, direction: -1 | 1): Promise<ClassActivity[]> {
  const res = await apiClient.post(`/api/v1/activities/${id}/move`, { direction });
  return res.data.activities;
}

export async function deleteActivity(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/activities/${id}`);
}

export async function goLive(id: string, stepIndex: number): Promise<ClassActivity> {
  const res = await apiClient.post(`/api/v1/activities/${id}/go-live`, { step_index: stepIndex });
  return res.data.activity;
}

export async function stopActivity(id: string): Promise<ClassActivity> {
  const res = await apiClient.post(`/api/v1/activities/${id}/stop`);
  return res.data.activity;
}

// WIP-012 (design §5.4) — the confidence summary shape ClassActivityService's
// `summary[step_key].confidence` carries. `by_correct` is present only when
// the step's `correct` field lets a response be scored right/wrong (D1,
// design §12) — today, 0 of 119 poll steps carry one, so the instructor sees
// `confidence` without `by_correct` and ConfidenceGrid.tsx renders the
// one-dimensional split instead of the 2×2.
export interface ConfidenceSummary {
  sure: number;
  unsure: number;
  answered: number;
  by_correct?: {
    right_sure: number;
    right_unsure: number;
    wrong_sure: number;
    wrong_unsure: number;
  };
}

/** One step's before/after distribution + the movement between them
 * (design §4.5, §11.4). Computed server-side from user ids — the client
 * never pairs rows itself. */
export interface RevoteSummaryFields {
  /** The round-1 step key this one re-votes. */
  of: string;
  before: { count: number; choices: Record<string, number> };
  after: { count: number; choices: Record<string, number> };
  /** Students who answered both rounds. */
  both: number;
  /** Of `both`, how many picked a different option the second time. */
  changed: number;
}

export interface ActivityResponseSummary {
  [stepKey: string]: {
    count: number;
    choices: Record<string, number>;
    // Extra fields the summarisers in backend/src/services/summary/ add per
    // step (design §11.4) — typed by the issue that owns each one:
    confidence?: ConfidenceSummary;
    /** design §6.6 — instructor-only, small_group activities only: counts
     * per group label, '' (empty string) is the ungrouped bucket. */
    groups?: Record<string, { count: number; choices: Record<string, number> }>;
    /**
     * Present only when the step carries a misconception_choices /
     * misconception_range pattern (WIP-014) AND at least one response has
     * matched it — a step with no pattern, or zero hits so far, has no
     * `misconception` key at all. Never trust an absent key as "0"; the
     * pill (MisconceptionPill.tsx) relies on that distinction (design §7.2).
     */
    misconception?: { count: number; students: { name: string | null }[] };
    revote?: RevoteSummaryFields;
    [extra: string]: unknown;
  };
}

export interface ActivityResponsesResult {
  responses: Array<{
    user: { id: string; student_id: string | null; name: string | null } | null;
    step_key: string;
    group_label: string | null;
    payload: Record<string, unknown>;
    submitted_at: string;
  }>;
  summary: ActivityResponseSummary;
}

export async function getResponses(activityId: string): Promise<ActivityResponsesResult> {
  const res = await apiClient.get(`/api/v1/activities/${activityId}/responses`);
  return res.data;
}

export function responsesCsvPath(activityId: string): string {
  return `/api/v1/activities/${activityId}/responses.csv`;
}

// ── live-activity set API (design §11.4, build order §2.2) — one line per
// issue. Each Phase 1/2 issue adds its own function(s) here; nothing above
// this line moves.
/** POST /activities/:id/revote — go live on the step linked to `stepKey`
 * (creating it on first use), design §4.2/§11.4. */
export async function revote(activityId: string, stepKey: string): Promise<ClassActivity> {
  const res = await apiClient.post(`/api/v1/activities/${activityId}/revote`, { step_key: stepKey });
  return res.data.activity;
}
// ── WIP-013 — class groups (design §6, §11.4) ───────────────────────────────

/** GET /class-groups/:sessionId, student shape. */
export interface ClassGroupsStudentView {
  groups: Array<{ label: string }>;
  my_group: MyGroup | null;
  self_select: boolean;
}

/** GET /class-groups/:sessionId, instructor shape. */
export interface ClassGroupRow {
  id: string;
  label: string;
  source: 'manual' | 'self' | 'generated';
  published: boolean;
  member_ids: string[];
  members: Array<{ id: string; student_id: string | null; name: string | null }>;
}

export interface ClassGroupsInstructorView {
  groups: ClassGroupRow[];
}

export async function getClassGroups(sessionId: string): Promise<ClassGroupsStudentView | ClassGroupsInstructorView> {
  const res = await apiClient.get(`/api/v1/class-groups/${sessionId}`);
  return res.data;
}

export async function saveClassGroups(
  sessionId: string,
  body: { labels: string[]; self_select: boolean; published: boolean },
): Promise<ClassGroupsInstructorView> {
  const res = await apiClient.put(`/api/v1/class-groups/${sessionId}`, body);
  return res.data;
}

export async function joinClassGroup(sessionId: string, label: string): Promise<{ my_group: MyGroup }> {
  const res = await apiClient.post(`/api/v1/class-groups/${sessionId}/join`, { label });
  return res.data;
}

// ── WIP-017 — group generation (design §6.3, §11.4) ─────────────────────────

/** POST /class-groups/:sessionId/generate — an unpublished, anti-repeat-
 * biased draft roster, plus how many pairings repeat a prior meeting's and
 * which group absorbed the odd student(s), if any. */
export interface GenerateClassGroupsResult extends ClassGroupsInstructorView {
  repeats: number;
  odd_group: string | null;
}

export async function generateClassGroups(sessionId: string, size: number): Promise<GenerateClassGroupsResult> {
  const res = await apiClient.post(`/api/v1/class-groups/${sessionId}/generate`, { size });
  return res.data;
}

/** PUT /class-groups/:sessionId, WIP-017's shape — the hand-edited roster
 * from the Groups… dialog after Generate. */
export async function saveClassGroupRoster(
  sessionId: string,
  body: { groups: Array<{ label: string; member_ids: string[] }>; published: boolean },
): Promise<ClassGroupsInstructorView> {
  const res = await apiClient.put(`/api/v1/class-groups/${sessionId}`, body);
  return res.data;
}

// WIP-011 (design §2, §11.4) — the projector's own step shape. Deliberately
// NOT `ActivityStep`: this is what survives the backend allowlist, not what
// an instructor payload may carry (no `correct`, no misconception fields —
// they were never picked in the first place).
export interface ProjectionStep {
  label: string;
  prompt: string;
  mode: StepMode;
  options?: string[];
  revote_of?: string;
}

export interface ProjectionRevote {
  before: Record<string, number>;
  after: Record<string, number>;
  both: number;
  changed: number;
}

export interface ProjectionPayload {
  session: { label: string; status: SessionStatus };
  activity: { title: string; grouping: Grouping } | null;
  step: ProjectionStep | null;
  state: 'idle' | 'open' | 'shown';
  count: number;
  expected: number | null;
  choices?: Record<string, number>;
  revote?: ProjectionRevote;
}

export async function getProjection(sessionId: string): Promise<ProjectionPayload> {
  const res = await apiClient.get(`/api/v1/projection/${sessionId}`);
  return res.data;
}

/** Show results closes the vote (409 vote_closed on further submits); reopen
 * voting undoes it (design §1 — one button, one phase, never live bars). */
export async function setResultsShown(activityId: string, shown: boolean): Promise<ClassActivity> {
  const res = await apiClient.post(`/api/v1/activities/${activityId}/results`, { shown });
  return res.data.activity;
}

/** Run-sheet activity tabs — the instructor's own completion checkbox.
 * Server-backed (migration 019), so it is the same "done" for every browser
 * this instructor opens the run-sheet from. */
export async function setActivityCompleted(activityId: string, completed: boolean): Promise<ClassActivity> {
  const res = await apiClient.post(`/api/v1/activities/${activityId}/complete`, { completed });
  return res.data.activity;
}

/** Run-sheet step stack — the per-STEP version of setActivityCompleted
 * (migration 021). Instructor-only, server-backed for the same reason: the
 * instructor may switch devices mid-class and "which cards have I run" has
 * to read the same on the next one. */
export async function setActivityStepDone(activityId: string, stepKey: string, done: boolean): Promise<ClassActivity> {
  const res = await apiClient.post(`/api/v1/activities/${activityId}/step-complete`, { step_key: stepKey, done });
  return res.data.activity;
}

// WIP-015: getWarmupSuggestion(sessionId) · acceptWarmupSuggestion(...) · dismissWarmupSuggestion(...)

// ── activity catalog (Teach → Activity types) ───────────────────────────────
// BP 22 — read-only reference: every activity format, how often the current
// 30-meeting catalogue actually uses it and where, what a student sees for
// it. Backed by GET /class-sessions/activity-catalog, which reads
// content/class-activities.json directly (no DB), so this reflects the
// authored plan even before Setup → Generate has ever seeded a session.

export interface ActivityCatalogMeetingRef {
  seq: number;
  /** Short chip code — "Intro", "L1A", "Review 2" — never the full title. */
  code: string;
  /** The full meeting label, e.g. "Lecture 1A – Understanding Matter, Elements, & Compounds". */
  label: string;
}

export interface ActivityCatalogFormat {
  format: ActivityFormat;
  /** How many authored activities use this format across the whole catalogue. */
  count: number;
  /** Activity-level `grouping` values, counted (e.g. { pair: 6, small_group: 3 }). */
  groupings: Partial<Record<Grouping, number>>;
  /** Activity-level `capture` values, counted. See step_modes for what a
   *  student can actually submit — capture can say "response" while every
   *  step is group-mode and shows no answer control at all. */
  captures: Partial<Record<Capture, number>>;
  /** The real per-STEP mode breakdown across every activity of this format —
   *  the ground truth for what renders an answer control on a student's
   *  screen (only poll/response steps do; group and instruct never do). */
  step_modes: Partial<Record<StepMode, number>>;
  duration_min: { min: number; max: number } | null;
  meetings: ActivityCatalogMeetingRef[];
}

export interface ActivityCatalogResult {
  totals: { activities: number; steps: number; meetings: number };
  formats: ActivityCatalogFormat[];
}

export async function getActivityCatalog(): Promise<ActivityCatalogResult> {
  const res = await apiClient.get('/api/v1/class-sessions/activity-catalog');
  return res.data;
}
